import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, CircularProgress, Alert,
} from '@mui/material';
import {
  PieChart, Pie, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';
import {
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import { learningAnalyticsAPI, AttendanceClusterData } from '../../services/learningAnalyticsApi';

const AttendanceClusterTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AttendanceClusterData | null>(null);
  const [year, setYear] = useState<string>('');
  const [district, setDistrict] = useState<string>('');
  const [level, setLevel] = useState<string>('');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      if (year) params.year = parseInt(year);
      if (district) params.district = district;
      if (level) params.level = parseInt(level);

      const response = await learningAnalyticsAPI.getAttendanceClusters(params);
      setData(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load attendance cluster data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [year, district, level]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
  }

  if (!data) return null;

  const { summary, cluster_distribution, day_of_week_student, schools } = data;

  // Unique districts from schools for filter dropdown
  const districts = Array.from(new Set(schools.map((s: any) => s.district_name))).sort();

  // Map cluster names to chip colors
  const getClusterChipColor = (cluster: string): string => {
    const found = cluster_distribution.find(c => c.cluster === cluster);
    return found?.color || '#9e9e9e';
  };

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="Year" value={year}
              onChange={(e) => setYear(e.target.value)}>
              <MenuItem value="">Current</MenuItem>
              {[2026, 2025, 2024, 2023, 2022].map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="District" value={district}
              onChange={(e) => setDistrict(e.target.value)}>
              <MenuItem value="">All Districts</MenuItem>
              {districts.map((d: any) => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="School Level" value={level}
              onChange={(e) => setLevel(e.target.value)}>
              <MenuItem value="">All Levels</MenuItem>
              <MenuItem value="3">Pre-Primary</MenuItem>
              <MenuItem value="1">Primary</MenuItem>
              <MenuItem value="2">Secondary</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <SchoolIcon color="primary" />
                <Typography variant="body2" color="textSecondary">Total Schools</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {summary.total_schools}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <AssessmentIcon sx={{ color: '#1976d2' }} />
                <Typography variant="body2" color="textSecondary">Overall Attendance Rate</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="primary">
                {summary.overall_attendance_rate.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CheckCircleIcon sx={{ color: '#388e3c' }} />
                <Typography variant="body2" color="textSecondary">Regular</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {summary.regular_pct.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <WarningIcon color="error" />
                <Typography variant="body2" color="textSecondary">Chronic</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="error.main">
                {summary.chronic_pct.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Cluster Distribution Pie Chart */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Cluster Distribution</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Schools grouped by attendance patterns.
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={cluster_distribution}
                    dataKey="count"
                    nameKey="cluster"
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    label={({ cluster, percentage }) => `${cluster} (${percentage.toFixed(1)}%)`}
                  >
                    {cluster_distribution.map((entry, idx) => (
                      <Cell key={idx} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: number, name: string) => [v, name]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Day-of-Week Student Attendance Bar Chart */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Day-of-Week Student Attendance</Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Average student attendance rate by day of the week.
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={day_of_week_student}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip formatter={(v: number) => `${v.toFixed(1)}%`} />
                  <Legend />
                  <Bar dataKey="attendance_rate" fill="#1976d2" name="Attendance Rate %" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Schools Table */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>School Attendance Clusters</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Individual schools and their assigned attendance cluster.
          </Typography>
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>School</TableCell>
                  <TableCell>District</TableCell>
                  <TableCell align="right">Attendance Rate</TableCell>
                  <TableCell align="center">Cluster</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schools.map((school: any) => (
                  <TableRow key={school.school_reg_number} hover>
                    <TableCell>{school.school_name}</TableCell>
                    <TableCell>{school.district_name}</TableCell>
                    <TableCell align="right">
                      {school.attendance_rate != null
                        ? `${school.attendance_rate.toFixed(1)}%`
                        : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={school.cluster}
                        size="small"
                        sx={{
                          bgcolor: getClusterChipColor(school.cluster),
                          color: '#fff',
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AttendanceClusterTab;
